<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer> 
                    <help-link appId="settings" pageId="access-control" color="white"></help-link>    
                </v-toolbar>
            </v-card>
            <v-card class="elavation-1 mt-5">
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-70 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search name, employee id, team" single-line hide-details></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? headers_mobile : headers"
                    :items="members_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                    <template v-slot:items="props">
                        <tr @click="viewUser(props.item.userId)" title="Click to view/edit access control" class="clickable">
                           <td>
                                <v-chip class="user-chip-container" small>
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.firstName}} {{props.item.lastName}}
                                </v-chip>
                            </td>
                            <td v-if="!isMobile">
                                <team-chips :teams="props.item.teams"></team-chips>
                            </td>
                            <td v-if="!isMobile">{{props.item.employeeId}}</td>
                            <td v-if="!isMobile">{{props.item.position}}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.employmentStatus)">{{commonfn.getStatusDescription(props.item.employmentStatus)}}</b>
                            </td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>
    </v-layout> 
</template>

<script>

import appsApi from '@/api/applications'
import commonfn from '@/common/functions'
import awsS3Helper from '@/helpers/aws-s3'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'

export default {     
    name: 'AccessControl',
    metaInfo: {
        title: 'Access Control',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Access Control' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'team-chips': () => import("@/components/Common/TeamsChip"),
        'help-link': () => import('@/components/Common/HelpLink'),
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)
        return {
            commonfn,
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: userPrefs.getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            headers: [
                {text: 'Employee' ,align:'left', value:'lastName', width: '10%', sortable:true},
                {text: 'Teams', align: 'left', value: 'teams', width: '10%', sortable:true},
                {text: 'Employee Id', align: 'left', value: 'employeeId', width: '5%', sortable:true},
                {text: 'Position', align: 'left', value:'position', width: '10%', sortable:true},
                {text: 'Employment', align: 'left', value: 'employmentStatus', width: '5%', sortable:true},
                {text: 'Access Control', align: 'left', value: 'status', width: '5%', sortable:true},
            ],
            headers_mobile: [
                {text: 'Employee' ,align:'left', value:'lastName', width: '10%', sortable:true},
                {text: 'Employment', align: 'left', value: 'employmentStatus', width: '5%', sortable:true},
                {text: 'Access', align: 'left', value: 'status', width: '5%', sortable:true},
            ],
            members: []
        }
    },
    methods: { 
        viewUser(userId) {
            this.$router.push(`/settings/permissions/${userId}`)
        },
        async getMembersByCompany() {
            try{
                this.loading = true
                const response = await appsApi.getAccessRightsSummary()
                if (response) {
                    this.members = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Access Control'
        })

        this.getMembersByCompany()
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            userPrefs.updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        isMobile() {
            return window.innerWidth < 600
        },
        members_filtered() {
            let temp = this.members

            // search handler
            if (this.search) {
                const searchLower = this.search.toLowerCase();

                temp = temp.filter(item => {
                    const firstName = item.firstName.toLowerCase().includes(searchLower)
                    const lastName = item.lastName.toLowerCase().includes(searchLower)
                    const employeeId = item.employeeId.toLowerCase().includes(searchLower)
                    const teamsMatch = Array.isArray(item.teams)
                        ? item.teams.join(', ').toLowerCase().includes(searchLower)
                        : false

                    return firstName || lastName || employeeId || teamsMatch 
                });
            }

            return temp;
        },
    }
}
</script>